import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { useAppSelector } from "@/redux/hooks";

import { useGetSendErrorsOverviewQuery } from "@/pages/Private/redux/support-dashboard/support-dashboard.api";
import { supportDashboardSelector } from "@/pages/Private/redux/support-dashboard/support-dashboard.slice";

type KpiTone = "error" | "warning" | "info" | "neutral";

interface KpiCardProps {
	label: string;
	value: number | string;
	hint?: string;
	tone: KpiTone;
}

const ACCENT_CLASSES: Record<KpiTone, string> = {
	error: "bg-status-error",
	warning: "bg-status-warning",
	info: "bg-status-info",
	neutral: "bg-inactive-item",
};

const VALUE_CLASSES: Record<KpiTone, string> = {
	error: "text-status-error",
	warning: "text-status-warning",
	info: "text-status-info",
	neutral: "text-text",
};

const KpiCard: FunctionComponent<KpiCardProps> = ({ label, value, hint, tone }) => (
	<div className="relative overflow-hidden rounded-xl border border-border-light bg-brand-white p-4">
		<div className={classNames("absolute bottom-0 left-0 top-0 w-1", ACCENT_CLASSES[tone])} />
		<p className="mb-1.5 text-xs text-text-meta">{label}</p>
		<p className={classNames("text-2xl font-bold leading-none", VALUE_CLASSES[tone])}>{value}</p>
		{hint && <p className="mt-2 text-[11px] text-text-meta">{hint}</p>}
	</div>
);

export const SendErrorsKpiStrip: FunctionComponent = () => {
	const { t } = useTranslation();

	const { sendErrorRange } = useAppSelector(supportDashboardSelector);

	const { data: overview } = useGetSendErrorsOverviewQuery(sendErrorRange);

	if (!overview) {
		return null;
	}

	const successRate =
		overview.stepsSentOk24h + overview.totalErrors24h === 0
			? 100
			: Math.round(
					(overview.stepsSentOk24h / (overview.stepsSentOk24h + overview.totalErrors24h)) * 100
				);

	return (
		<div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
			<KpiCard
				hint={t("supportDashboard.kpis.sendErrorsHint", {
					companies: overview.affectedCompanies,
				})}
				label={t("supportDashboard.kpis.sendErrors")}
				tone="error"
				value={overview.needsAttention24h}
			/>
			<KpiCard
				hint={t("supportDashboard.kpis.inmailUndeliveredHint")}
				label={t("supportDashboard.kpis.inmailUndelivered")}
				tone="warning"
				value={overview.inmailUndelivered24h}
			/>
			<KpiCard
				hint={t("supportDashboard.kpis.disconnectedHint")}
				label={t("supportDashboard.kpis.disconnected")}
				tone="info"
				value={overview.disconnectedAccounts}
			/>
			<KpiCard
				hint={t("supportDashboard.kpis.sentOkHint", { rate: successRate })}
				label={t("supportDashboard.kpis.sentOk")}
				tone="neutral"
				value={overview.stepsSentOk24h}
			/>
		</div>
	);
};
